// screens/Homepage.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTheme } from "../hooks/useTheme";
import { useAuth } from "../providers/AuthProvider";
import { useNavigate } from "react-router-dom";
import Footer from "../components/Footer";
import DataPackageCard from "../components/DataPackageCard";
import {
  ArrowRight,
  ChevronRight,
  Wifi,
  ShoppingBag,
  Clock,
  Zap,
  ShieldCheck,
  Truck,
  BadgeCheck,
  Star,
  Sparkles,
} from "lucide-react";

const PHRASES = [
  { prefix: "Affordable", highlight: "data", suffix: "with instant delivery." },
  { prefix: "Wholesale", highlight: "market goods", suffix: "for less." },
  { prefix: "Group", highlight: "pre-orders", suffix: "that save you more." },
  { prefix: "One place", highlight: "for campus life", suffix: "in Ghana." },
];

export default function Homepage() {
  const { colors } = useTheme();
  const { authFetch } = useAuth();
  const navigate = useNavigate();

  const [phraseIndex, setPhraseIndex] = useState(0);
  const [packages, setPackages] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  // Responsive
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isMobile = width <= 768;
  const isTablet = width > 768 && width <= 1024;

  // Phrase rotator
  useEffect(() => {
    const t = setInterval(
      () => setPhraseIndex((p) => (p + 1) % PHRASES.length),
      4200
    );
    return () => clearInterval(t);
  }, []);

  // Fetch data packages
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await authFetch(
          "/data/packages/?is_active=true&page_size=6"
        );
        const json = await res.json();
        if (cancelled) return;
        if (res.ok && json.status === "success") {
          setPackages(
            (json.data || []).map((pkg) => ({
              id: pkg.id,
              network: pkg.network,
              size: `${pkg.size_gb}GB`,
              price: parseFloat(
                pkg.effective_price || pkg.base_price_customer
              ),
              priceDisplay: `₵${parseFloat(
                pkg.effective_price || pkg.base_price_customer
              ).toFixed(2)}`,
              originalPrice: pkg.base_price_customer
                ? parseFloat(pkg.base_price_customer)
                : null,
              validity: "30 Days",
              popular: pkg.is_popular || pkg.sales_count > 20,
              price_source: pkg.price_source || "base",
              is_active: pkg.is_active,
            }))
          );
        }
      } catch (e) {
        console.error("packages:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authFetch]);

  // Fetch marketplace products + pre-order flags
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [prodRes, campRes] = await Promise.all([
          authFetch("/marketplace/products/"),
          authFetch("/marketplace/pre-orders/?is_active=true"),
        ]);
        const prodJson = await prodRes.json();
        const campJson = await campRes.json();
        if (cancelled || !prodRes.ok || prodJson.status !== "success") return;

        const preorderIds = new Set();
        if (campRes.ok) {
          (campJson.data || []).forEach((c) =>
            (c.pre_order_products || []).forEach((it) =>
              preorderIds.add(String(it.product))
            )
          );
        }

        setProducts(
          (prodJson.data || []).slice(0, 8).map((p) => {
            const isPre =
              preorderIds.has(String(p.id)) || p.sale_type === "PRE_ORDER";
            const rawPrice = isPre
              ? p.effective_pre_order_price || p.pre_order_price_customer
              : p.effective_price || p.base_price_customer;
            return {
              id: p.id,
              name: p.name,
              price: Number(rawPrice || 0).toFixed(2),
              isPreOrder: isPre,
              inStock: !!p.is_in_stock,
              rating: Number(p.average_rating || 0),
              reviews: p.review_count || 0,
              image:
                p.thumbnail_image_url || p.main_image_url || p.main_image,
            };
          })
        );
      } catch (e) {
        console.error("products:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authFetch]);

  const handlePackageClick = useCallback(
    (pkg) => navigate(`/data/purchase/${pkg.id}`),
    [navigate]
  );

  const phrase = PHRASES[phraseIndex];

  return (
    <div
      style={{
        background: colors.mainBg,
        color: colors.text,
        minHeight: "100vh",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        overflowX: "hidden",
      }}
    >
      <div
        style={{
          maxWidth: 1180,
          margin: "0 auto",
          padding: isMobile ? "16px 16px 0" : "32px 24px 0",
        }}
      >
        {/* ── HERO ──────────────────────────────────────────────── */}
        <section
          style={{
            paddingTop: isMobile ? 8 : 24,
            paddingBottom: isMobile ? 32 : 56,
          }}
        >
          {/* Rotating eyebrow */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 12px 6px 8px",
              borderRadius: 999,
              background: colors.raisedBg,
              border: `1px solid ${colors.border}`,
              marginBottom: isMobile ? 18 : 24,
              fontSize: isMobile ? 12 : 13,
              fontWeight: 600,
              color: colors.secondaryText,
              transition: "opacity 0.4s ease",
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 22,
                height: 22,
                borderRadius: "50%",
                background: colors.hoverBg,
                color: colors.linkColor,
              }}
            >
              <Sparkles size={12} />
            </span>
            <span key={phraseIndex} style={{ animation: "fadeSwap 0.4s ease" }}>
              {phrase.prefix}{" "}
              <span style={{ color: colors.heading }}>{phrase.highlight}</span>{" "}
              {phrase.suffix}
            </span>
          </div>

          {/* Headline */}
          <h1
            style={{
              fontSize: isMobile
                ? "clamp(28px, 7vw, 40px)"
                : "clamp(40px, 5vw, 60px)",
              fontWeight: 800,
              letterSpacing: "-0.03em",
              lineHeight: 1.08,
              color: colors.heading,
              margin: 0,
              maxWidth: 780,
            }}
          >
            {isMobile ? (
              <>
                Data & market goods,
                <br />
                <span style={{ color: colors.linkColor }}>
                  delivered with speed.
                </span>
              </>
            ) : (
              <>
                Everything for campus life,{" "}
                <span style={{ color: colors.linkColor }}>
                  delivered with speed.
                </span>
              </>
            )}
          </h1>

          <p
            style={{
              fontSize: isMobile ? 15 : 17,
              lineHeight: 1.55,
              color: colors.secondaryText,
              margin: isMobile ? "14px 0 22px" : "20px 0 32px",
              maxWidth: 560,
            }}
          >
            Affordable data bundles and wholesale marketplace deals — with
            guaranteed prompt delivery across Ghana.
          </p>

          {/* Primary CTA row */}
          <div
            style={{
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
              marginBottom: isMobile ? 28 : 40,
            }}
          >
            <button
              onClick={() => navigate("/data")}
              style={primaryBtn(colors, isMobile)}
              onMouseEnter={btnHover(colors, true)}
              onMouseLeave={btnLeave(colors)}
            >
              <Wifi size={isMobile ? 15 : 17} />
              Buy Data
              <ArrowRight size={isMobile ? 14 : 16} />
            </button>
            <button
              onClick={() => navigate("/marketplace")}
              style={secondaryBtn(colors, isMobile)}
              onMouseEnter={btnHover(colors, false)}
              onMouseLeave={btnLeave(colors)}
            >
              <ShoppingBag size={isMobile ? 15 : 17} />
              Browse Market
            </button>
          </div>

          {/* Trust strip */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: isMobile ? 14 : 28,
              color: colors.secondaryText,
              fontSize: isMobile ? 12.5 : 13.5,
              fontWeight: 500,
            }}
          >
            {[
              { icon: BadgeCheck, label: "Instant delivery" },
              { icon: ShieldCheck, label: "Secure payments" },
              { icon: Truck, label: "Nationwide coverage" },
            ].map(({ icon: Icon, label }) => (
              <span
                key={label}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <Icon size={isMobile ? 14 : 15} color={colors.success} />
                {label}
              </span>
            ))}
          </div>
        </section>

        {/* ── TWO WORLDS ────────────────────────────────────────── */}
        <section style={{ marginBottom: isMobile ? 36 : 56 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: isMobile ? 12 : 20,
            }}
          >
            <WorldCard
              colors={colors}
              isMobile={isMobile}
              icon={Wifi}
              accent={colors.linkColor}
              accentBg={colors.hoverBg}
              title="Data"
              tagline="Instant bundles on MTN · Telecel · AT"
              bullets={["Delivered in seconds", "Lowest rates in Ghana"]}
              onClick={() => navigate("/data")}
            />
            <WorldCard
              colors={colors}
              isMobile={isMobile}
              icon={ShoppingBag}
              accent={colors.success}
              accentBg={colors.successBg}
              title="Market"
              tagline="Instant orders & timed pre-order windows"
              bullets={["Shop now or join a group buy", "Wholesale prices, campus delivery"]}
              onClick={() => navigate("/marketplace")}
            />
          </div>
        </section>

        {/* ── POPULAR DATA ──────────────────────────────────────── */}
        <Section
          title="Popular data"
          subtitle="Instant delivery · best rates"
          action={() => navigate("/data")}
          actionLabel="See all"
          colors={colors}
          isMobile={isMobile}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr 1fr"
                : isTablet
                ? "repeat(3, 1fr)"
                : "repeat(4, 1fr)",
              gap: isMobile ? 10 : 16,
            }}
          >
            {loading
              ? Array.from({ length: isMobile ? 4 : 4 }).map((_, i) => (
                  <SkeletonCard key={i} colors={colors} isMobile={isMobile} />
                ))
              : packages
                  .slice(0, isMobile ? 4 : 4)
                  .map((pkg) => (
                    <DataPackageCard
                      key={pkg.id}
                      package={pkg}
                      network={pkg.network}
                      onClick={() => handlePackageClick(pkg)}
                      showPriceSource={pkg.price_source !== "base"}
                    />
                  ))}
          </div>
        </Section>

        {/* ── FEATURED PRODUCTS ─────────────────────────────────── */}
        {products.length > 0 && (
          <Section
            title="From the market"
            subtitle="Live deals & pre-orders"
            action={() => navigate("/marketplace")}
            actionLabel="View all"
            colors={colors}
            isMobile={isMobile}
            last
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr 1fr"
                  : isTablet
                  ? "repeat(3, 1fr)"
                  : "repeat(4, 1fr)",
                gap: isMobile ? 10 : 16,
              }}
            >
              {products
                .slice(0, isMobile ? 4 : 8)
                .map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    colors={colors}
                    isMobile={isMobile}
                    onPress={() =>
                      navigate(
                        p.isPreOrder
                          ? "/marketplace?filter=preorder"
                          : `/marketplace/products/${p.id}`
                      )
                    }
                  />
                ))}
            </div>
          </Section>
        )}
      </div>

      <Footer />

      <style>{`
        @keyframes fadeSwap {
          from { opacity: 0; transform: translateY(-3px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmer {
          0% { opacity: 0.6; }
          50% { opacity: 0.35; }
          100% { opacity: 0.6; }
        }
      `}</style>
    </div>
  );
}

/* ── HELPERS ──────────────────────────────────────────────── */

const primaryBtn = (colors, isMobile) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: isMobile ? "12px 18px" : "14px 24px",
  borderRadius: 12,
  background: colors.btnBg,
  color: colors.btnText,
  border: "none",
  fontWeight: 600,
  fontSize: isMobile ? 14 : 15,
  cursor: "pointer",
  boxShadow: `0 6px 20px ${colors.btnBg}33`,
  transition: "transform 0.15s ease, box-shadow 0.15s ease",
});

const secondaryBtn = (colors, isMobile) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: isMobile ? "12px 18px" : "14px 24px",
  borderRadius: 12,
  background: colors.raisedBg,
  color: colors.text,
  border: `1px solid ${colors.border}`,
  fontWeight: 600,
  fontSize: isMobile ? 14 : 15,
  cursor: "pointer",
  transition: "transform 0.15s ease, border-color 0.15s ease",
});

const btnHover = (colors, isPrimary) => (e) => {
  e.currentTarget.style.transform = "translateY(-1px)";
  if (isPrimary) {
    e.currentTarget.style.boxShadow = `0 10px 28px ${colors.btnBg}55`;
  } else {
    e.currentTarget.style.borderColor = colors.btnBg;
  }
};

const btnLeave = (colors) => (e) => {
  e.currentTarget.style.transform = "translateY(0)";
  if (e.currentTarget.style.boxShadow) {
    e.currentTarget.style.boxShadow = `0 6px 20px ${colors.btnBg}33`;
  }
};

const WorldCard = ({
  colors,
  isMobile,
  icon: Icon,
  accent,
  accentBg,
  title,
  tagline,
  bullets,
  onClick,
}) => (
  <button
    onClick={onClick}
    style={{
      textAlign: "left",
      padding: isMobile ? 20 : 28,
      borderRadius: 20,
      background: colors.cardBg,
      border: `1px solid ${colors.border}`,
      cursor: "pointer",
      transition: "transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
      boxShadow: `0 2px 12px ${colors.shadow}`,
      display: "flex",
      flexDirection: "column",
      gap: isMobile ? 14 : 18,
      position: "relative",
      overflow: "hidden",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-3px)";
      e.currentTarget.style.borderColor = accent;
      e.currentTarget.style.boxShadow = `0 14px 40px ${colors.shadow}`;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.borderColor = colors.border;
      e.currentTarget.style.boxShadow = `0 2px 12px ${colors.shadow}`;
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <div
        style={{
          width: isMobile ? 42 : 50,
          height: isMobile ? 42 : 50,
          borderRadius: 14,
          background: accentBg,
          color: accent,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon size={isMobile ? 20 : 24} />
      </div>
      <ChevronRight size={20} color={colors.secondaryText} />
    </div>

    <div>
      <h3
        style={{
          margin: 0,
          fontSize: isMobile ? 20 : 24,
          fontWeight: 800,
          letterSpacing: "-0.02em",
          color: colors.heading,
        }}
      >
        {title}
      </h3>
      <p
        style={{
          margin: "6px 0 0",
          fontSize: isMobile ? 13 : 14,
          color: colors.secondaryText,
          lineHeight: 1.5,
        }}
      >
        {tagline}
      </p>
    </div>

    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {bullets.map((b) => (
        <span
          key={b}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontSize: isMobile ? 12.5 : 13,
            color: colors.text,
          }}
        >
          <span
            style={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              background: accent,
              flexShrink: 0,
            }}
          />
          {b}
        </span>
      ))}
    </div>
  </button>
);

const Section = ({
  title,
  subtitle,
  action,
  actionLabel,
  colors,
  isMobile,
  last,
  children,
}) => (
  <section style={{ marginBottom: last ? (isMobile ? 32 : 56) : isMobile ? 36 : 56 }}>
    <div
      style={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-between",
        gap: 12,
        marginBottom: isMobile ? 16 : 22,
      }}
    >
      <div>
        <h2
          style={{
            margin: 0,
            fontSize: isMobile ? 18 : 22,
            fontWeight: 800,
            letterSpacing: "-0.02em",
            color: colors.heading,
          }}
        >
          {title}
        </h2>
        {subtitle && (
          <p
            style={{
              margin: "4px 0 0",
              fontSize: isMobile ? 12.5 : 13.5,
              color: colors.secondaryText,
            }}
          >
            {subtitle}
          </p>
        )}
      </div>
      {action && (
        <button
          onClick={action}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: isMobile ? 13 : 14,
            fontWeight: 600,
            color: colors.linkColor,
            padding: 0,
            whiteSpace: "nowrap",
          }}
        >
          {actionLabel}
          <ArrowRight size={14} />
        </button>
      )}
    </div>
    {children}
  </section>
);

const SkeletonCard = ({ colors, isMobile }) => (
  <div
    style={{
      borderRadius: 16,
      background: colors.cardBg,
      border: `1px solid ${colors.border}`,
      padding: isMobile ? 14 : 18,
      height: isMobile ? 150 : 190,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      animation: "shimmer 1.6s ease-in-out infinite",
    }}
  >
    <div
      style={{
        height: isMobile ? 44 : 56,
        width: isMobile ? 44 : 56,
        borderRadius: 12,
        background: colors.border,
      }}
    />
    <div>
      <div
        style={{
          height: 12,
          width: "70%",
          borderRadius: 6,
          background: colors.border,
          marginBottom: 8,
        }}
      />
      <div
        style={{
          height: 20,
          width: "45%",
          borderRadius: 6,
          background: colors.border,
        }}
      />
    </div>
  </div>
);

const ProductCard = ({ product, colors, isMobile, onPress }) => (
  <div
    onClick={onPress}
    style={{
      background: colors.raisedBg,
      border: `1px solid ${colors.border}`,
      borderRadius: 16,
      overflow: "hidden",
      cursor: "pointer",
      transition: "transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
      boxShadow: `0 2px 10px ${colors.shadow}`,
      display: "flex",
      flexDirection: "column",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-3px)";
      e.currentTarget.style.borderColor = colors.btnBg;
      e.currentTarget.style.boxShadow = `0 12px 32px ${colors.shadow}`;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.borderColor = colors.border;
      e.currentTarget.style.boxShadow = `0 2px 10px ${colors.shadow}`;
    }}
  >
    {/* Image */}
    <div
      style={{
        position: "relative",
        height: isMobile ? 130 : 170,
        background: colors.mainBg,
        overflow: "hidden",
      }}
    >
      {product.image ? (
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transition: "transform 0.5s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.04)")}
          onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
        />
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: colors.secondaryText,
          }}
        >
          <ShoppingBag size={28} />
        </div>
      )}

      {/* Small status pill */}
      <span
        style={{
          position: "absolute",
          top: 10,
          left: 10,
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: "3px 9px",
          borderRadius: 999,
          background: product.isPreOrder
            ? colors.warningBg
            : product.inStock
            ? colors.successBg
            : colors.errorBg,
          color: product.isPreOrder
            ? colors.warning
            : product.inStock
            ? colors.success
            : colors.error,
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: 0.2,
          textTransform: "uppercase",
        }}
      >
        {product.isPreOrder ? <Clock size={11} /> : <Zap size={11} />}
        {product.isPreOrder ? "Pre-order" : product.inStock ? "In stock" : "Sold out"}
      </span>
    </div>

    {/* Body */}
    <div
      style={{
        padding: isMobile ? "12px 12px 14px" : "14px 16px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        flex: 1,
      }}
    >
      <h4
        style={{
          margin: 0,
          fontSize: isMobile ? 13 : 14.5,
          fontWeight: 600,
          color: colors.heading,
          lineHeight: 1.35,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          minHeight: isMobile ? 34 : 38,
        }}
      >
        {product.name}
      </h4>

      {product.reviews > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            fontSize: 11.5,
            color: colors.secondaryText,
          }}
        >
          <Star size={12} fill={colors.warning} color={colors.warning} />
          <span style={{ fontWeight: 600, color: colors.text }}>
            {product.rating.toFixed(1)}
          </span>
          <span>({product.reviews})</span>
        </div>
      )}

      <div
        style={{
          marginTop: "auto",
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <span
          style={{
            fontSize: isMobile ? 16 : 18,
            fontWeight: 800,
            color: colors.heading,
            letterSpacing: "-0.02em",
          }}
        >
          ₵{product.price}
        </span>
        <span
          style={{
            fontSize: isMobile ? 11.5 : 12.5,
            fontWeight: 600,
            color: colors.linkColor,
            display: "inline-flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          View
          <ChevronRight size={13} />
        </span>
      </div>
    </div>
  </div>
);